import  { useRef, useEffect, useState, useMemo, useCallback, useContext } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { CalendarIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { DataContext } from "../DataContext";

// UI splits
import ControlsBar from "./ControlsBar";
import SatelliteOverlayControls from "./SatelliteOverlayControls";
import CalendarModal from "./CalendarModal";
import MobileDataPopup from "./MobileDataPopup";
import "./MapPopup.css";

// The main interactive map component. Handles rendering the Mapbox map, 
// overlays, location selection, date filtering, grid overlays, and
// integrates all map-related UI components (controls, overlays, popups, legend, etc.).

function Map({ onSelectLocation, onDateRangeChange, onFilteredDataChange }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const [locations, setLocations] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [filteredMeasurements, setFilteredMeasurements] = useState([]);
  const [isCustomRange, setIsCustomRange] = useState(false);

  const todayStr = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedRangeLabel, setSelectedRangeLabel] = useState(t("map_page.time_periods.week"));
  const [sliderTicks, setSliderTicks] = useState([]);
  const [sliderIndex, setSliderIndex] = useState(0);

  // Grid overlay state
  const [showGridOverlay, setShowGridOverlay] = useState(false);
  const [gridDataType, setGridDataType] = useState('sst'); // 'sst' or 'chlorophyll'
  const [gridData, setGridData] = useState(null);
  const [isLoadingGrid, setIsLoadingGrid] = useState(false);
  const [gridResolution, setGridResolution] = useState(0.5); // Default resolution

  // Pull cached data from DataContext
  const { locations: ctxLocations, measurements: ctxMeasurements } = useContext(DataContext);
  useEffect(() => {
    if (Array.isArray(ctxLocations)) setLocations(ctxLocations);
    if (Array.isArray(ctxMeasurements)) setMeasurements(ctxMeasurements);
  }, [ctxLocations, ctxMeasurements]);

  // Helper: is SST available (>= 2018)
  const isSSTAvailable = useCallback(() => {
    if (!sliderTicks || sliderTicks.length === 0) return true;
    const currentTick = sliderTicks[sliderIndex];
    if (!currentTick || !currentTick.start) return true;
    const yearMatch = currentTick.start.match(/(\d{4})/);
    const selectedYear = yearMatch ? parseInt(yearMatch[1]) : new Date().getFullYear();
    return selectedYear >= 2018;
  }, [sliderTicks, sliderIndex]);

  // Abort/debounce refs for grid fetch
  const gridAbortControllerRef = useRef(null);
  const gridDebounceTimeoutRef = useRef(null);

  const monthNames = useMemo(
    () => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    []
  );

  // Mobile detection and state management
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const [showMobileDataPopup, setShowMobileDataPopup] = useState(false);
  const [selectedPopupLocation, setSelectedPopupLocation] = useState(null);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile controls when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setShowMobileControls(false);
    }
  }, [isMobile]);

  // Fetch real grid data (with cancellation)
  const fetchGridData = useCallback(async (dataType) => {
    if (gridAbortControllerRef.current) {
      gridAbortControllerRef.current.abort();
    }
    gridAbortControllerRef.current = new AbortController();
    const signal = gridAbortControllerRef.current.signal;

    setIsLoadingGrid(true);
    try {
      const apiBase =
        process.env.REACT_APP_LOCATIONS_API?.replace('/locations', '') ||
        process.env.REACT_APP_MEASUREMENTS_API?.replace('/measurements', '') ||
        'http://localhost:8000';

      const currentTick = sliderTicks[sliderIndex];
      const selectedDate = currentTick?.start || new Date().toISOString().slice(0, 10);

      const [baseUrl, queryParams] = apiBase.split('?');
      const gridUrl = queryParams
        ? `${baseUrl}/satellite/grid/${dataType}?${queryParams}&resolution=${gridResolution}&date=${selectedDate}`
        : `${baseUrl}/satellite/grid/${dataType}?resolution=${gridResolution}&date=${selectedDate}`;

      const response = await fetch(gridUrl, { signal });

      if (signal.aborted) return;

      if (response.ok) {
        const gridData = await response.json();
        setGridData(gridData);
        return gridData;
      } else {
        setGridData(null);
      }
    } catch (error) {
      if (error.name !== 'AbortError') setGridData(null);
    } finally {
      if (!signal.aborted) setIsLoadingGrid(false);
    }
  }, [sliderTicks, sliderIndex, gridResolution]);

  // Debounced grid fetch
  const debouncedFetchGridData = useCallback((dataType, delay = 300) => {
    if (gridDebounceTimeoutRef.current) {
      clearTimeout(gridDebounceTimeoutRef.current);
    }
    gridDebounceTimeoutRef.current = setTimeout(() => {
      if (showGridOverlay) {
        fetchGridData(dataType);
      }
    }, delay);
  }, [showGridOverlay, fetchGridData]);

  // Cleanup abort/timeout on unmount
  useEffect(() => {
    return () => {
      if (gridAbortControllerRef.current) gridAbortControllerRef.current.abort();
      if (gridDebounceTimeoutRef.current) clearTimeout(gridDebounceTimeoutRef.current);
    };
  }, []);

  // Hide SST overlay when not available for year
  useEffect(() => {
    if (showGridOverlay && gridDataType === 'sst' && !isSSTAvailable()) {
      setShowGridOverlay(false);
      if (mapRef.current?.getLayer('satellite-grid')) {
        mapRef.current.setLayoutProperty('satellite-grid-background', 'visibility', 'none');
        mapRef.current.setLayoutProperty('satellite-no-data-pattern', 'visibility', 'none');
        mapRef.current.setLayoutProperty('satellite-grid', 'visibility', 'none');
        mapRef.current.setLayoutProperty('satellite-grid-borders', 'visibility', 'none');
      }
    }
  }, [sliderIndex, sliderTicks, showGridOverlay, gridDataType, isSSTAvailable]);

  // Toggle grid overlay
  const toggleGridOverlay = async (dataType) => {
    if (showGridOverlay && gridDataType === dataType) {
      setShowGridOverlay(false);
      if (mapRef.current?.getLayer('satellite-grid')) {
        mapRef.current.setLayoutProperty('satellite-grid-background', 'visibility', 'none');
        mapRef.current.setLayoutProperty('satellite-no-data-pattern', 'visibility', 'none');
        mapRef.current.setLayoutProperty('satellite-grid', 'visibility', 'none');
        mapRef.current.setLayoutProperty('satellite-grid-borders', 'visibility', 'none');
      }
    } else {
      setGridDataType(dataType);
      if (!showGridOverlay || gridDataType !== dataType) {
        await fetchGridData(dataType);
      }
      setShowGridOverlay(true);
    }
  };

  // Recompute ticks & filteredMeasurements whenever we slide or pick calendar
  useEffect(() => {
    let ticks = [];
    let result = [];

    if (!isCustomRange) {
      // SLIDER MODE
      if (selectedRangeLabel === t("map_page.time_periods.week")) {
        const now = new Date();
        for (let i = 3; i >= 0; i--) {
          const end = new Date(now);
          end.setDate(now.getDate() - 7 * i);
          const start = new Date(end);
          start.setDate(end.getDate() - 6);
          const s = start.toISOString().slice(0, 10);
          const e = end.toISOString().slice(0, 10);
          ticks.push({
            label: `${monthNames[start.getMonth()]} ${start.getDate()}–${monthNames[end.getMonth()]} ${end.getDate()}`,
            start: s,
            end: e
          });
        }
      } else if (selectedRangeLabel === t("map_page.time_periods.month")) {
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
          const d = new Date(now);
          d.setMonth(now.getMonth() - i, 1);
          const s = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
          const e = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
          ticks.push({
            label: `${monthNames[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`,
            start: s,
            end: e
          });
        }
      } else if (selectedRangeLabel === t("map_page.time_periods.year")) {
        const years = [...new Set(measurements.map(m => new Date(m.date).getFullYear()))].sort((a, b) => a - b);
        ticks = years.map(year => ({
          label: year.toString(),
          start: `${year}-01-01`,
          end: `${year}-12-31`
        }));
      }

      if (ticks[sliderIndex]) {
        const { start, end } = ticks[sliderIndex];
        result = measurements.filter(m => m.date >= start && m.date <= end);
        onDateRangeChange(start, end);
      }
    } else {
      // CUSTOM RANGE MODE
      const eDate = new Date(endDate);

      if (selectedRangeLabel === t("map_page.time_periods.week")) {
        for (let i = 3; i >= 0; i--) {
          const end = new Date(eDate);
          end.setDate(eDate.getDate() - 7 * i);
          const start = new Date(end);
          start.setDate(end.getDate() - 6);
          ticks.push({
            label: `${monthNames[start.getMonth()]} ${start.getDate()}–${monthNames[end.getMonth()]} ${end.getDate()}`,
            start: start.toISOString().slice(0, 10),
            end: end.toISOString().slice(0, 10)
          });
        }
      } else if (selectedRangeLabel === t("map_page.time_periods.month")) {
        for (let i = 11; i >= 0; i--) {
          const d = new Date(eDate);
          d.setMonth(eDate.getMonth() - i, 1);
          const s = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
          const e = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
          ticks.push({
            label: `${monthNames[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`,
            start: s,
            end: e
          });
        }
      } else if (selectedRangeLabel === t("map_page.time_periods.year")) {
        const years = [...new Set(measurements.map(m => new Date(m.date).getFullYear()))].sort((a, b) => a - b);
        ticks = years.map(year => ({
          label: year.toString(),
          start: `${year}-01-01`,
          end: `${year}-12-31`
        }));
      }

      if (ticks[sliderIndex]) {
        const { start, end } = ticks[sliderIndex];
        result = measurements.filter(m => m.date >= start && m.date <= end);
        onDateRangeChange(start, end);
      }
    }

    setSliderTicks(ticks);
    setFilteredMeasurements(result);

    // push geojson up for any external consumers
    const enriched = result
      .map(m => {
        const loc = locations.find(l => l.id === m.location_id);
        return (
          loc && {
            type: "Feature",
            properties: {
              name: loc.name,
              id: loc.id,
              amount: m.value,
              date: m.date,
              toxin: m.toxin_type || ""
            },
            geometry: { type: "Point", coordinates: [loc.longitude, loc.latitude] }
          }
        );
      })
      .filter(Boolean);

    onFilteredDataChange(enriched);
  }, [
    selectedRangeLabel,
    sliderIndex,
    measurements,
    locations,
    startDate,
    endDate,
    monthNames,
    onDateRangeChange,
    onFilteredDataChange,
    isCustomRange,
    t
  ]);

  // reset knob to first tick when slider type changes
  useEffect(() => {
    setSliderIndex(0);
  }, [selectedRangeLabel]);

  // Mapbox init + layers + popup
  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: "mapbox://styles/mapbox/standard",
        center: [-8.112104, 39.041345],
        zoom: 6.2,
        minZoom: 6.2,
        maxBounds: [[-15, 35], [-1, 43]],
      });

      mapRef.current.getCanvas().addEventListener("contextmenu", (ev) => ev.preventDefault());

      mapRef.current.on("load", () => {
        mapRef.current.addSource("pollution-data", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });

        // REMOVED heatmap layer

        // circle layer – small uniform blue points
        mapRef.current.addLayer({
          id: "pollution-points",
          type: "circle",
          source: "pollution-data",
          paint: {
            "circle-radius": 5,
            "circle-color": "#3b82f6",         // blue-500
            "circle-stroke-color": "#1e40af",  // blue-800
            "circle-stroke-width": 1,
            "circle-opacity": 0.9
          },
          interactive: false
        });

        mapRef.current.addLayer({
          id: "pollution-points-hitbox",
          type: "circle",
          source: "pollution-data",
          paint: {
            "circle-radius": 5,
            "circle-color": "rgba(0,0,0,0)"
          }
        });

        // satellite grid
        mapRef.current.addSource("satellite-grid", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] }
        });

        mapRef.current.addLayer({
          id: "satellite-grid-background",
          type: "background",
          paint: { "background-color": "rgba(80, 80, 80, 0.15)" },
          layout: { visibility: "none" }
        });

        mapRef.current.addLayer({
          id: "satellite-no-data-pattern",
          type: "background",
          paint: { "background-color": "rgba(100, 100, 100, 0.2)" },
          layout: { visibility: "none" }
        });

        mapRef.current.addLayer({
          id: "satellite-grid",
          type: "fill",
          source: "satellite-grid",
          paint: {
            "fill-opacity": 0.3,
            "fill-color": [
              "interpolate",
              ["linear"],
              ["get", "value"],
              0, "#000080",
              10, "#0000FF",
              15, "#00FFFF",
              20, "#00FF00",
              25, "#FFFF00",
              30, "#FF8000",
              35, "#FF0000"
            ]
          },
          layout: { visibility: "none" }
        });

        mapRef.current.addLayer({
          id: "satellite-grid-borders",
          type: "line",
          source: "satellite-grid",
          paint: { "line-color": "#ffffff", "line-width": 1.0, "line-opacity": 0.6 },
          layout: { visibility: "none" }
        });

        // click popup
        mapRef.current.on("click", "pollution-points-hitbox", (e) => {
          const { name, id } = e.features[0].properties;
          const coords = e.lngLat;

          // Check if mobile - show detailed popup, otherwise show simple popup
          if (window.innerWidth < 1024) {
            // Mobile: Show detailed data popup
            setSelectedPopupLocation({ name, id });
            setShowMobileDataPopup(true);
            setShowMobileControls(false); // Close mobile controls if open
          } else {
            // Desktop: Show simple popup as before
            const popupContent = document.createElement("div");
            popupContent.className = "custom-popup";
            popupContent.innerHTML = `
              <div class="popup-header">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="#4a5568" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <h3>${name}</h3>
              </div>
            `;

            document.querySelectorAll(".mapboxgl-popup").forEach(p => p.remove());
            const popup = new mapboxgl.Popup({ closeButton: false, offset: 25, className: "fancy-popup" })
              .setLngLat(coords)
              .setDOMContent(popupContent)
              .addTo(mapRef.current);

            setTimeout(() => popup.remove(), 5000);
          }

          const multi = !!(
            e.originalEvent &&
            (e.originalEvent.metaKey || e.originalEvent.ctrlKey || e.originalEvent.shiftKey)
          );
          onSelectLocation?.({ name, id }, multi);
        });

        mapRef.current.on("mouseenter", "pollution-points-hitbox", () => {
          mapRef.current.getCanvas().style.cursor = "pointer";
        });
        mapRef.current.on("mouseleave", "pollution-points-hitbox", () => {
          mapRef.current.getCanvas().style.cursor = "";
        });
      });
    }
  }, [onSelectLocation, monthNames, t]);

  // Push new geojson whenever filteredMeasurements change — ALWAYS one feature per measurement
  useEffect(() => {
    if (!mapRef.current?.getSource("pollution-data")) return;

    const geo = filteredMeasurements
      .map(m => {
        const loc = locations.find(l => l.id === m.location_id);
        return (
          loc && {
            type: "Feature",
            properties: { name: loc.name, id: loc.id, amount: m.value, date: m.date, toxin: m.toxin_type || "" },
            geometry: { type: "Point", coordinates: [loc.longitude, loc.latitude] }
          }
        );
      })
      .filter(Boolean);

    mapRef.current.getSource("pollution-data").setData({
      type: "FeatureCollection",
      features: geo
    });
  }, [filteredMeasurements, locations]);

  // Update grid overlay when gridData changes
  useEffect(() => {
    if (!mapRef.current?.getSource("satellite-grid") || !gridData) return;

    mapRef.current.getSource("satellite-grid").setData(gridData);

    const values = gridData.features?.map(f => f.properties.value).filter(v => v != null) || [];

    let colorScale;
    if (values.length === 0) {
      colorScale = ["interpolate", ["linear"], ["get", "value"], 0, "#000080", 1, "#FF0000"];
    } else {
      const sortedValues = [...values].sort((a, b) => a - b);
      const n = sortedValues.length;
      const getPercentile = (p) => {
        const index = Math.max(0, Math.min(n - 1, Math.floor(p * n)));
        return sortedValues[index];
      };

      const p0 = getPercentile(0.0);
      const p15 = getPercentile(0.15);
      const p30 = getPercentile(0.30);
      const p50 = getPercentile(0.50);
      const p70 = getPercentile(0.70);
      const p85 = getPercentile(0.85);
      const p100 = getPercentile(1.0);

      if (gridDataType === 'sst') {
        colorScale = [
          "interpolate", ["linear"], ["get", "value"],
          p0, "#000080",
          p15, "#0000FF",
          p30, "#00FFFF",
          p50, "#00FF00",
          p70, "#FFFF00",
          p85, "#FF8000",
          p100, "#FF0000"
        ];
      } else {
        colorScale = [
          "interpolate", ["linear"], ["get", "value"],
          p0, "#000080",
          p15, "#0000FF",
          p30, "#00FFFF",
          p50, "#00FF00",
          p70, "#FFFF00",
          p85, "#FF8000",
          p100, "#FF0000"
        ];
      }
    }

    mapRef.current.setPaintProperty("satellite-grid", "fill-color", colorScale);

    const visibility = showGridOverlay ? "visible" : "none";
    mapRef.current.setLayoutProperty("satellite-grid-background", "visibility", visibility);
    mapRef.current.setLayoutProperty("satellite-no-data-pattern", "visibility", visibility);
    mapRef.current.setLayoutProperty("satellite-grid", "visibility", visibility);
    mapRef.current.setLayoutProperty("satellite-grid-borders", "visibility", visibility);
  }, [gridData, showGridOverlay, gridDataType]);

  // Refresh grid when slider date changes
  useEffect(() => {
    if (showGridOverlay && sliderTicks.length > 0) {
      debouncedFetchGridData(gridDataType, 150);
    }
  }, [sliderIndex, sliderTicks, showGridOverlay, gridDataType, gridResolution, debouncedFetchGridData]);

  // Submit calendar: switch to custom-range mode, pre-filter & set sliderIndex
  const submitDateRange = () => {
    if (new Date(startDate) > new Date(endDate)) {
      return alert(t("map_page.alert.date_error"));
    }

    const filtered = measurements.filter(m => {
      const d = new Date(m.date);
      return d >= new Date(startDate) && d <= new Date(endDate);
    });
    if (!filtered.length) alert(t("map_page.alert.no_points"));

    setFilteredMeasurements(filtered);
    setIsCustomRange(true);
    setIsCalendarOpen(false);

    if (selectedRangeLabel === t("map_page.time_periods.week")) {
      setSliderIndex(3);
    } else if (selectedRangeLabel === t("map_page.time_periods.month")) {
      setSliderIndex(11);
    } else if (selectedRangeLabel === t("map_page.time_periods.year")) {
      const endY = new Date(endDate).getFullYear();
      const years = [...new Set(measurements.map(m => new Date(m.date).getFullYear()))].sort((a, b) => a - b);
      const idx = years.findIndex(y => y === endY);
      setSliderIndex(idx >= 0 ? idx : years.length - 1);
    }

    onDateRangeChange(startDate, endDate);
  };

  return (
    <div className={`relative w-full flex flex-col ${isMobile ? 'h-screen' : 'h-[50vh] lg:h-screen'}`}>
      {/* Controls bar (hidden on mobile, shown lg+) */}
      <ControlsBar
        isMobile={isMobile}
        CalendarIcon={CalendarIcon}
        onOpenCalendar={() => setIsCalendarOpen(true)}
        selectedRangeLabel={selectedRangeLabel}
        setSelectedRangeLabel={setSelectedRangeLabel}
        sliderTicks={sliderTicks}
        sliderIndex={sliderIndex}
        setSliderIndex={setSliderIndex}
        t={t}
      />

      {/* Satellite Grid Overlay Controls - Hidden on mobile, shown on desktop */}
      {!isMobile && (
        <SatelliteOverlayControls
          isLoadingGrid={isLoadingGrid}
          showGridOverlay={showGridOverlay}
          gridDataType={gridDataType}
          toggleGridOverlay={toggleGridOverlay}
          sliderTicks={sliderTicks}
          sliderIndex={sliderIndex}
          gridResolution={gridResolution}
          setGridResolution={setGridResolution}
          debouncedFetchGridData={debouncedFetchGridData}
          gridData={gridData}
          t={t}
          isSSTAvailable={isSSTAvailable}
          mapRef={mapRef}
        />
      )}

      {/* Map */}
      <div className="flex-1">
        <div ref={mapContainerRef} className="h-full w-full bg-[lightgrey]" />
      </div>

      {/* Legend For Map not in Use Right Now
      <MapLegend
        showGridOverlay={showGridOverlay}
        gridDataType={gridDataType}
        gridData={gridData}
      /> */}

      {/* Mobile Controls Button - Floating Action Button */}
      {isMobile && !showMobileControls && (
        <button
          onClick={() => setShowMobileControls(true)}
          className="fixed bottom-20 right-4 z-40 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          style={{ 
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)' 
          }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </button>
      )}

      {/* Mobile Controls Panel */}
      {isMobile && showMobileControls && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setShowMobileControls(false)}
          />
          
          {/* Control Panel */}
          <div className="fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t("map_page.controls.map_controls")}</h3>
              <button
                onClick={() => setShowMobileControls(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Controls Content */}
            <div className="p-4 space-y-6">
              {/* Time Period Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t("map_page.controls.time_period")}</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setSelectedRangeLabel(t("map_page.time_periods.week"))}
                    className={`p-2 text-sm rounded-md border ${
                      selectedRangeLabel === t("map_page.time_periods.week")
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t("map_page.time_periods.week")}
                  </button>
                  <button
                    onClick={() => setSelectedRangeLabel(t("map_page.time_periods.month"))}
                    className={`p-2 text-sm rounded-md border ${
                      selectedRangeLabel === t("map_page.time_periods.month")
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t("map_page.time_periods.month")}
                  </button>
                  <button
                    onClick={() => setSelectedRangeLabel(t("map_page.time_periods.year"))}
                    className={`p-2 text-sm rounded-md border ${
                      selectedRangeLabel === t("map_page.time_periods.year")
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t("map_page.time_periods.year")}
                  </button>
                </div>
              </div>

              {/* Time Slider */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("map_page.controls.time_range")}: {sliderTicks[sliderIndex]?.label || t("map_page.controls.loading")}
                </label>
                <input
                  type="range"
                  min={0}
                  max={sliderTicks.length - 1}
                  step={1}
                  value={sliderIndex}
                  onChange={(e) => setSliderIndex(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{sliderTicks[0]?.label}</span>
                  <span>{sliderTicks[sliderTicks.length - 1]?.label}</span>
                </div>
              </div>

              {/* Calendar Custom Range */}
              <div>
                <button
                  onClick={() => {
                    setIsCalendarOpen(true);
                    setShowMobileControls(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <CalendarIcon className="w-5 h-5" />
                  {t("map_page.controls.custom_date_range")}
                </button>
              </div>

              {/* Satellite Data Controls */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t("map_page.satellite.satellite_data")}</label>
                <div className="space-y-2">
                  {isSSTAvailable() && (
                    <button
                      onClick={() => toggleGridOverlay('sst')}
                      className={`w-full p-3 text-left rounded-md border ${
                        showGridOverlay && gridDataType === 'sst'
                          ? 'bg-red-50 border-red-200 text-red-800'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-red-500">●</span>
                        {t("map_page.satellite.sea_surface_temperature")}
                        {isLoadingGrid && gridDataType === 'sst' && (
                          <div className="ml-auto">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          </div>
                        )}
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => toggleGridOverlay('chlorophyll')}
                    className={`w-full p-3 text-left rounded-md border ${
                      showGridOverlay && gridDataType === 'chlorophyll'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-green-500">●</span>
                      {t("map_page.satellite.chlorophyll_concentration")}
                      {isLoadingGrid && gridDataType === 'chlorophyll' && (
                        <div className="ml-auto">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        </div>
                      )}
                    </div>
                  </button>
                </div>
                
                {/* Grid Resolution Slider for Mobile */}
                {showGridOverlay && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      {t("map_page.satellite.grid_resolution")}: {gridResolution}°
                    </label>
                    <input
                      type="range"
                      min="0.025"
                      max="1.0"
                      step="0.025"
                      value={gridResolution}
                      onChange={(e) => {
                        const newResolution = parseFloat(e.target.value);
                        setGridResolution(newResolution);
                        debouncedFetchGridData(gridDataType, 200);
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1 text-xs text-gray-500">
                      <span>0.025° ({t("map_page.satellite.ultra")})</span>
                      <span>1.0° ({t("map_page.satellite.fast")})</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Data Popup */}
      <MobileDataPopup
        location={selectedPopupLocation}
        isOpen={showMobileDataPopup}
        onClose={() => {
          setShowMobileDataPopup(false);
          setSelectedPopupLocation(null);
        }}
        filteredMeasurements={filteredMeasurements}
        locations={locations}
      />

      {/* Calendar modal */}
      <CalendarModal
        open={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        submitDateRange={submitDateRange}
        t={t}
      />
    </div>
  );
}

export default Map;
      